import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { ArrowRight, Sparkles, Brain, CheckCircle2, Calculator, Lock, Star, Zap, Target, PlayCircle } from 'lucide-react';
import { DjayMascot, DjaySpeechBubble } from './DjayMascot';
/* ──────────────────────────────────────────────────────────────────
 * Sticker — small rotated badge for "pop"
 * ────────────────────────────────────────────────────────────────── */
function Sticker({
  children,
  rotate = -4,
  className = ''
}: {
  children: React.ReactNode;
  rotate?: number;
  className?: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.8,
    rotate: rotate * 2
  }} animate={{
    opacity: 1,
    scale: 1,
    rotate
  }} transition={{
    delay: 0.3,
    type: 'spring',
    stiffness: 200
  }} whileHover={{
    rotate: 0,
    scale: 1.08
  }} className={`inline-flex items-center gap-1.5 ${className}`} data-id="element-282">
      {children}
    </motion.div>;
}
/* ──────────────────────────────────────────────────────────────────
 * Hero with live, clickable demo
 * ────────────────────────────────────────────────────────────────── */
export function HeroSection() {
  const ref = useRef<HTMLElement>(null);
  const {
    scrollY
  } = useScroll();
  const copyY = useTransform(scrollY, [0, 600], [0, -50]);
  const previewY = useTransform(scrollY, [0, 600], [0, -130]);
  const blobAY = useTransform(scrollY, [0, 800], [0, 240]);
  const blobBY = useTransform(scrollY, [0, 800], [0, -200]);
  return <section ref={ref} className="relative pt-28 pb-20 lg:pt-36 lg:pb-32 overflow-hidden" data-id="element-283">
      {/* Mesh gradient blobs */}
      <motion.div aria-hidden="true" className="absolute -top-32 -left-20 w-[44rem] h-[44rem] rounded-full bg-orange-400/30 blur-[120px] pointer-events-none" style={{
      y: blobAY
    }} data-id="element-284" />
      <motion.div aria-hidden="true" className="absolute top-20 -right-40 w-[40rem] h-[40rem] rounded-full bg-amber-300/30 blur-[120px] pointer-events-none" style={{
      y: blobBY
    }} data-id="element-285" />
      <div aria-hidden="true" className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle at 1px 1px, #000 1px, transparent 0)',
      backgroundSize: '22px 22px'
    }} data-id="element-286" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-287">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-14 items-center" data-id="element-288">
          {/* ──────── Left: Copy ──────── */}
          <motion.div style={{
          y: copyY
        }} className="relative" data-id="element-289">
            {/* Floating sticker chip */}
            <Sticker rotate={-3} className="bg-slate-900 text-white rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-widest shadow-lg shadow-slate-900/20 mb-6" data-id="element-290">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" data-id="element-291" />
              Vroege toegang · groep 6, 7 & 8
            </Sticker>

            {/* Big editorial headline */}
            <h1 className="font-black text-slate-900 leading-[0.95] tracking-tight mb-7 text-5xl sm:text-6xl lg:text-[5.25rem]" data-id="element-292">
              <span className="block" data-id="element-293">De digitale</span>
              <span className="block" data-id="element-294">
                <span className="relative inline-block" data-id="element-295">
                  <span className="relative z-10" data-id="element-296">leraar</span>
                  <motion.svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 14" preserveAspectRatio="none" initial={{
                  pathLength: 0
                }} animate={{
                  pathLength: 1
                }} transition={{
                  delay: 0.7,
                  duration: 1.2,
                  ease: 'easeOut'
                }} data-id="element-297">
                    <motion.path d="M 4 8 Q 50 0 100 6 T 196 5" stroke="#F97316" strokeWidth="6" strokeLinecap="round" fill="none" initial={{
                    pathLength: 0
                  }} animate={{
                    pathLength: 1
                  }} transition={{
                    delay: 0.7,
                    duration: 1.2
                  }} data-id="element-298" />
                  </motion.svg>
                </span>{' '}
                die{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-br from-orange-500 via-amber-500 to-rose-500" data-id="element-299">
                  meegroeit
                </span>
              </span>
              <span className="block text-slate-900" data-id="element-300">met je kind.</span>
            </h1>

            <p className="text-base sm:text-lg lg:text-xl text-slate-600 mb-8 leading-relaxed max-w-xl font-medium" data-id="element-301">
              Geen quiz. Geen game. Elke vraag bij je kind is{' '}
              <strong className="text-slate-900" data-id="element-302">8 keer gekeurd</strong>, past
              zich aan <strong className="text-slate-900" data-id="element-303">het niveau</strong>{' '}
              aan, en volgt het echte Nederlandse lesplan. Voor 10 vakken. Geen
              reclame, geen ranglijsten.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-8" data-id="element-304">
              <motion.a href="#aanmelden" whileHover={{
              y: -2
            }} whileTap={{
              y: 1
            }} className="group relative inline-flex justify-center items-center gap-2 bg-slate-900 text-white rounded-2xl font-bold px-7 py-4 text-base shadow-[0_8px_0_0_rgb(15,23,42),0_24px_40px_-12px_rgba(15,23,42,0.5)] hover:shadow-[0_4px_0_0_rgb(15,23,42),0_20px_30px_-12px_rgba(15,23,42,0.5)] transition-shadow" data-id="element-305">
                <span className="absolute inset-0 rounded-2xl bg-gradient-to-r from-orange-500/0 via-orange-500/30 to-orange-500/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity" data-id="element-306" />
                <Zap className="relative w-4 h-4 text-orange-300" strokeWidth={3} data-id="element-307" />
                <span className="relative" data-id="element-308">Word vroege tester</span>
                <ArrowRight className="relative w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-309" />
              </motion.a>
              <motion.a href="#probeer" whileHover={{
              y: -2
            }} whileTap={{
              y: 1
            }} className="inline-flex justify-center items-center gap-2 bg-white text-slate-700 border-2 border-slate-200 rounded-2xl font-bold px-7 py-4 text-base shadow-[0_4px_0_0_rgb(226,232,240)] hover:border-slate-300 transition-colors" data-id="element-310">
                <PlayCircle className="w-4 h-4 text-orange-500" data-id="element-311" />
                Probeer een vraag
              </motion.a>
            </div>

            {/* Quick proof chips */}
            <div className="flex flex-wrap gap-2 mb-8" data-id="element-312">
              {[{
              icon: '🇳🇱',
              label: 'Op het lesplan'
            }, {
              icon: '🔒',
              label: 'Privacy voorop'
            }, {
              icon: '🚫',
              label: 'Geen reclame'
            }, {
              icon: '⭐',
              label: 'CITO-klaar'
            }].map((chip, i) => <motion.div key={chip.label} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5 + i * 0.08
            }} className="inline-flex items-center gap-1.5 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-full px-3 py-1.5 text-xs font-bold text-slate-700 shadow-sm" data-id="element-313">
                  <span data-id="element-314">{chip.icon}</span>
                  {chip.label}
                </motion.div>)}
            </div>

            {/* Mascot intro at bottom */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1.1
          }} className="flex items-center gap-3" data-id="element-315">
              <DjayMascot size={56} mood="encouraging" data-id="element-316" />
              <DjaySpeechBubble color="orange" data-id="element-317">
                Hoi! Ik ben djay. Klaar om met je kind te oefenen? 🚀
              </DjaySpeechBubble>
            </motion.div>
          </motion.div>

          {/* ──────── Right: Live preview ──────── */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} style={{
          y: previewY
        }} className="relative" data-id="element-318">
            <IntelligencePreview data-id="element-319" />
          </motion.div>
        </div>

        {/* Scroll cue — subtiel signaal "er is meer" */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.6,
        duration: 0.8
      }} className="hidden md:flex justify-center mt-20 pointer-events-none" aria-hidden="true" data-id="element-320">
          <motion.div animate={{
          y: [0, 8, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="flex flex-col items-center gap-1.5 text-slate-400" data-id="element-321">
            <span className="text-[10px] font-black uppercase tracking-[0.25em]" data-id="element-322">
              Scroll
            </span>
            <svg width="14" height="22" viewBox="0 0 14 22" fill="none" data-id="element-323">
              <rect x="1" y="1" width="12" height="20" rx="6" stroke="currentColor" strokeWidth="1.5" data-id="element-324" />
              <motion.circle cx="7" cy="7" r="1.5" fill="currentColor" animate={{
              cy: [7, 14, 7]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-325" />
            </svg>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}
/* ──────────────────────────────────────────────────────────────────
 * Interactive live preview — clickable answer on first frame
 * ────────────────────────────────────────────────────────────────── */
type FrameId = 'fraction' | 'numberline' | 'clock' | 'feedback';
const FRAMES: FrameId[] = ['fraction', 'numberline', 'clock', 'feedback'];
function IntelligencePreview() {
  const [frame, setFrame] = useState<FrameId>('fraction');
  const [answered, setAnswered] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  // Mouse tilt
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, {
    stiffness: 120,
    damping: 18
  });
  const sy = useSpring(my, {
    stiffness: 120,
    damping: 18
  });
  const rotateY = useTransform(sx, [0, 1], [-10, 10]);
  const rotateX = useTransform(sy, [0, 1], [8, -8]);
  // Auto-cycle frames (pauses when user has answered)
  useEffect(() => {
    if (answered) return;
    const t = setInterval(() => {
      setFrame(f => {
        const idx = FRAMES.indexOf(f);
        return FRAMES[(idx + 1) % FRAMES.length];
      });
    }, 3200);
    return () => clearInterval(t);
  }, [answered]);
  function handleMove(e: React.MouseEvent<HTMLDivElement>) {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  }
  function handleLeave() {
    mx.set(0.5);
    my.set(0.5);
  }
  const frameIdx = FRAMES.indexOf(frame);
  const masteryByFrame = [0.35, 0.5, 0.65, 0.8];
  const mastery = masteryByFrame[frameIdx];
  return <div className="relative w-full max-w-md mx-auto" style={{
    perspective: 1400
  }} data-id="element-326">
      {/* Glow ring */}
      <motion.div aria-hidden="true" className="absolute -inset-10 bg-gradient-to-tr from-orange-300/40 via-amber-200/30 to-rose-300/30 blur-[80px] rounded-[4rem] -z-10" animate={{
      scale: [1, 1.06, 1],
      rotate: [0, 3, 0]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-327" />

      <motion.div ref={cardRef} onMouseMove={handleMove} onMouseLeave={handleLeave} className="relative bg-white rounded-[2.25rem] border-4 border-white shadow-2xl overflow-hidden will-change-transform" style={{
      rotateX,
      rotateY,
      transformStyle: 'preserve-3d'
    }} data-id="element-328">
        {/* Browser chrome */}
        <div className="h-9 bg-slate-50 border-b border-slate-100 flex items-center px-4 gap-2" data-id="element-329">
          <div className="flex gap-1.5" data-id="element-330">
            <div className="w-2.5 h-2.5 rounded-full bg-rose-400" data-id="element-331" />
            <div className="w-2.5 h-2.5 rounded-full bg-amber-400" data-id="element-332" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" data-id="element-333" />
          </div>
          <div className="flex-1 text-center" data-id="element-334">
            <div className="inline-block px-3 py-0.5 rounded-md bg-white text-[10px] font-bold text-slate-400 border border-slate-100" data-id="element-335">
              meesterdjay.nl/oefenen
            </div>
          </div>
          <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest" data-id="element-336">
            ● Live
          </span>
        </div>

        {/* App canvas */}
        <div className="p-6 min-h-[460px] bg-gradient-to-b from-[#FFF9F2] to-white relative" data-id="element-337">
          {/* Header strip */}
          <div className="flex items-center justify-between mb-5" data-id="element-338">
            <div className="flex items-center gap-2" data-id="element-339">
              <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center" data-id="element-340">
                <Calculator className="w-4 h-4 text-orange-600" strokeWidth={2.5} data-id="element-341" />
              </div>
              <div data-id="element-342">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider leading-none" data-id="element-343">
                  Rekenen
                </div>
                <div className="text-xs font-black text-slate-800 leading-tight" data-id="element-344">
                  Breuken optellen · niveau 8
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5" data-id="element-345">
              <Target className="w-3 h-3 text-orange-500" strokeWidth={3} data-id="element-346" />
              <span className="text-[10px] font-black text-slate-700" data-id="element-347">
                Vraag {frameIdx + 1}/8
              </span>
            </div>
          </div>

          {/* Mastery ring */}
          <div className="flex items-center gap-4 mb-5" data-id="element-348">
            <div className="relative w-14 h-14" data-id="element-349">
              <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56" data-id="element-350">
                <circle cx="28" cy="28" r="22" fill="none" stroke="#F1F5F9" strokeWidth="6" data-id="element-351" />
                <motion.circle cx="28" cy="28" r="22" fill="none" stroke="#F97316" strokeWidth="6" strokeLinecap="round" strokeDasharray={2 * Math.PI * 22} initial={false} animate={{
                strokeDashoffset: 2 * Math.PI * 22 - 2 * Math.PI * 22 * mastery
              }} transition={{
                duration: 1,
                ease: 'easeOut'
              }} data-id="element-352" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-353">
                <motion.span key={mastery} initial={{
                opacity: 0,
                y: 5
              }} animate={{
                opacity: 1,
                y: 0
              }} className="text-xs font-black text-slate-800" data-id="element-354">
                  {Math.round(mastery * 100)}%
                </motion.span>
              </div>
            </div>
            <div className="flex-1" data-id="element-355">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-356">
                Beheersing
              </div>
              <div className="text-xs font-bold text-slate-600" data-id="element-357">
                Groeit met elk antwoord
              </div>
            </div>
            <Sticker rotate={6} className="bg-amber-100 border-2 border-amber-300 rounded-xl px-2 py-1 text-[9px] font-black text-amber-800 uppercase tracking-widest" data-id="element-358">
              <Sparkles className="w-3 h-3" data-id="element-359" /> +12%
            </Sticker>
          </div>

          {/* Animated question */}
          <AnimatePresence mode="wait" data-id="element-360">
            <motion.div key={frame} initial={{
            opacity: 0,
            y: 20,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -20,
            scale: 0.98
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" style={{
            transform: 'translateZ(20px)'
          }} data-id="element-361">
              {frame === 'fraction' && <FractionFrame answered={answered} onAnswer={opt => setAnswered(opt)} data-id="element-362" />}
              {frame === 'numberline' && <NumberLineFrame data-id="element-363" />}
              {frame === 'clock' && <ClockFrame data-id="element-364" />}
              {frame === 'feedback' && <FeedbackFrame data-id="element-365" />}
            </motion.div>
          </AnimatePresence>

          {/* Adaptive hint */}
          <motion.div key={`hint-${frame}`} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5
        }} className="mt-3 flex items-center gap-2 text-[10px] font-bold text-slate-500" data-id="element-366">
            <Brain className="w-3 h-3 text-orange-500" data-id="element-367" />
            <span data-id="element-368">
              {frame === 'fraction' && 'Vraag aangepast aan jouw niveau (tier 8)'}
              {frame === 'numberline' && 'Volgende: getallenlijn — verdiepende stap'}
              {frame === 'clock' && 'Klokkijken — herhaling om vast te zetten'}
              {frame === 'feedback' && 'Misconceptie herkend — gerichte uitleg'}
            </span>
          </motion.div>
        </div>
      </motion.div>

      {/* Floating side cards */}
      <motion.div className="absolute -left-6 lg:-left-14 top-16 hidden md:block bg-white rounded-2xl shadow-xl p-3 border border-slate-100 max-w-[190px]" initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.9
    }} style={{
      transform: 'translateZ(40px) rotate(-3deg)'
    }} data-id="element-369">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-370">
          <div className="flex items-center gap-2 mb-1" data-id="element-371">
            <div className="w-6 h-6 rounded-lg bg-emerald-100 flex items-center justify-center" data-id="element-372">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" data-id="element-373" />
            </div>
            <span className="text-[10px] font-black text-slate-800 uppercase tracking-wider" data-id="element-374">
              Goedgekeurd
            </span>
          </div>
          <div className="text-[10px] font-bold text-slate-500 leading-tight" data-id="element-375">
            Deze vraag is door alle 8 kwaliteitschecks heen
          </div>
        </motion.div>
      </motion.div>

      <motion.div className="absolute -right-2 lg:-right-10 -bottom-6 hidden md:block bg-slate-900 rounded-2xl shadow-xl p-3 border border-slate-800 max-w-[200px] text-white" initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 1.1
    }} style={{
      transform: 'translateZ(60px) rotate(3deg)'
    }} data-id="element-376">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        duration: 4.5,
        repeat: Infinity,
        delay: 0.5,
        ease: 'easeInOut'
      }} data-id="element-377">
          <div className="flex items-center gap-2 mb-1" data-id="element-378">
            <Sparkles className="w-3.5 h-3.5 text-amber-400" data-id="element-379" />
            <span className="text-[10px] font-black uppercase tracking-wider" data-id="element-380">
              Volgende stap
            </span>
          </div>
          <div className="text-[10px] font-bold text-slate-300 leading-tight" data-id="element-381">
            Gekozen op basis van wat je kind al kan
          </div>
        </motion.div>
      </motion.div>
    </div>;
}
/* ──────────────────────────────────────────────────────────────────
 * Mini renderers — small, decorative versions for the hero preview
 * ────────────────────────────────────────────────────────────────── */
function FractionFrame({
  answered,
  onAnswer
}: {
  answered: string | null;
  onAnswer: (opt: string) => void;
}) {
  const correct = '3/4';
  return <div data-id="element-382">
      <div className="text-xs font-bold text-slate-500 mb-3" data-id="element-383">
        Hoeveel is <span className="font-black text-slate-800" data-id="element-384">¼ + 2/4</span>?
      </div>
      <div className="flex items-center justify-center gap-3 mb-4" data-id="element-385">
        <FractionPie filled={1} total={4} color="#F97316" data-id="element-386" />
        <span className="text-xl font-black text-slate-400" data-id="element-387">+</span>
        <FractionPie filled={2} total={4} color="#F59E0B" data-id="element-388" />
        <span className="text-xl font-black text-slate-400" data-id="element-389">=</span>
        <FractionPie filled={3} total={4} color="#10B981" data-id="element-390" />
      </div>
      <div className="flex gap-2 justify-center" data-id="element-391">
        {['2/4', '3/4', '1/2'].map(opt => {
        const isCorrect = opt === correct;
        const picked = opt === answered;
        return <motion.button key={opt} whileTap={{
          scale: 0.92
        }} onClick={() => onAnswer(opt)} className={`px-3 py-1.5 rounded-lg text-xs font-black border-2 transition-colors ${picked && isCorrect ? 'bg-emerald-500 border-emerald-600 text-white' : picked && !isCorrect ? 'bg-rose-50 border-rose-300 text-rose-700' : answered && isCorrect ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'}`} data-id="element-392">
              {opt}
              {picked && isCorrect && <CheckCircle2 className="w-3 h-3 inline ml-1" data-id="element-393" />}
            </motion.button>;
      })}
      </div>
      {answered && <motion.div initial={{
      opacity: 0,
      y: 5
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mt-3 text-[10px] font-bold text-emerald-700 text-center" data-id="element-394">
          {answered === correct ? '🎉 Goed gedaan!' : '👀 Bijna — probeer nog eens'}
        </motion.div>}
    </div>;
}
function FractionPie({
  filled,
  total,
  color
}: {
  filled: number;
  total: number;
  color: string;
}) {
  const size = 38;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 2;
  return <svg width={size} height={size} data-id="element-395">
      <circle cx={cx} cy={cy} r={r} fill="#FEF3C7" data-id="element-396" />
      {Array.from({
      length: total
    }).map((_, i) => {
      const startAngle = i * 360 / total - 90;
      const endAngle = (i + 1) * 360 / total - 90;
      const startRad = startAngle * Math.PI / 180;
      const endRad = endAngle * Math.PI / 180;
      const x1 = cx + r * Math.cos(startRad);
      const y1 = cy + r * Math.sin(startRad);
      const x2 = cx + r * Math.cos(endRad);
      const y2 = cy + r * Math.sin(endRad);
      return <path key={i} d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2} Z`} fill={i < filled ? color : 'transparent'} stroke="#92400E" strokeWidth="1" data-id="element-397" />;
    })}
    </svg>;
}
function NumberLineFrame() {
  return <div data-id="element-398">
      <div className="text-xs font-bold text-slate-500 mb-3" data-id="element-399">
        Waar staat <span className="font-black text-slate-800" data-id="element-400">2,5</span> op de
        getallenlijn?
      </div>
      <div className="relative h-12 mb-2" data-id="element-401">
        <div className="absolute top-6 left-0 right-0 h-0.5 bg-slate-300" data-id="element-402" />
        {[0, 1, 2, 3, 4, 5].map((n, i) => <div key={n} className="absolute top-4 -translate-x-1/2" style={{
        left: `${i / 5 * 100}%`
      }} data-id="element-403">
            <div className="w-0.5 h-4 bg-slate-400 mx-auto" data-id="element-404" />
            <div className="text-[10px] font-bold text-slate-500 mt-1" data-id="element-405">{n}</div>
          </div>)}
        <motion.div className="absolute top-3 -translate-x-1/2" initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        type: 'spring',
        stiffness: 300
      }} style={{
        left: '50%'
      }} data-id="element-406">
          <div className="w-3 h-3 rounded-full bg-orange-500 ring-4 ring-orange-200 mx-auto" data-id="element-407" />
          <div className="text-[10px] font-black text-orange-600 mt-1" data-id="element-408">2,5</div>
        </motion.div>
      </div>
    </div>;
}
function ClockFrame() {
  const hours = 8;
  const minutes = 15;
  const hourAngle = hours % 12 * 30 + minutes / 60 * 30;
  const minuteAngle = minutes * 6;
  return <div data-id="element-409">
      <div className="text-xs font-bold text-slate-500 mb-3 text-center" data-id="element-410">
        Hoe laat is het?{' '}
        <span className="font-black text-slate-800" data-id="element-411">Kwart over 8</span>
      </div>
      <div className="flex justify-center" data-id="element-412">
        <div className="relative" style={{
        width: 120,
        height: 120
      }} data-id="element-413">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white to-slate-50 shadow-md border-4 border-slate-100" data-id="element-414" />
          <div className="absolute inset-1 rounded-full border-2 border-slate-200/60" data-id="element-415" />

          {Array.from({
          length: 12
        }).map((_, i) => {
          const num = i + 1;
          const rotation = num * 30;
          const radius = 42;
          const angleRad = (rotation - 90) * (Math.PI / 180);
          const x = 60 + radius * Math.cos(angleRad);
          const y = 60 + radius * Math.sin(angleRad);
          return <div key={num} className="absolute flex items-center justify-center font-black text-slate-700" style={{
            width: 14,
            height: 14,
            left: x - 7,
            top: y - 7,
            fontSize: '0.7rem'
          }} data-id="element-416">
                {num}
              </div>;
        })}

          <motion.div className="absolute z-10 rounded-full" style={{
          width: 5,
          height: 34,
          left: 'calc(50% - 2.5px)',
          bottom: '50%',
          borderRadius: '4px 4px 0 0',
          background: 'linear-gradient(to bottom, #1e293b 0%, #334155 100%)',
          transformOrigin: 'center bottom'
        }} initial={{
          rotate: 0
        }} animate={{
          rotate: hourAngle
        }} transition={{
          type: 'spring',
          stiffness: 80,
          damping: 14,
          delay: 0.2
        }} data-id="element-417" />
          <motion.div className="absolute z-20 rounded-full" style={{
          width: 4,
          height: 46,
          left: 'calc(50% - 2px)',
          bottom: '50%',
          borderRadius: '4px 4px 0 0',
          background: 'linear-gradient(to bottom, #f97316 0%, #ea580c 100%)',
          transformOrigin: 'center bottom'
        }} initial={{
          rotate: 0
        }} animate={{
          rotate: minuteAngle
        }} transition={{
          type: 'spring',
          stiffness: 80,
          damping: 14,
          delay: 0.4
        }} data-id="element-418" />
          <div className="absolute rounded-full z-30" style={{
          width: 10,
          height: 10,
          left: 'calc(50% - 5px)',
          top: 'calc(50% - 5px)',
          background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
          border: '2px solid white'
        }} data-id="element-419" />
        </div>
      </div>
    </div>;
}
function FeedbackFrame() {
  return <div data-id="element-420">
      <div className="flex items-start gap-3 mb-3" data-id="element-421">
        <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center shrink-0" data-id="element-422">
          <span className="text-base" data-id="element-423">💡</span>
        </div>
        <div data-id="element-424">
          <div className="text-[10px] font-black text-rose-700 uppercase tracking-wider mb-1" data-id="element-425">
            Hier maken kinderen vaak een foutje
          </div>
          <div className="text-xs font-bold text-slate-700 leading-snug" data-id="element-426">
            Bij gelijke noemers tel je alleen de tellers op — de noemer blijft
            staan.
          </div>
        </div>
      </div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5" data-id="element-427">
        <div className="text-[10px] font-black text-emerald-700 uppercase tracking-wider mb-0.5" data-id="element-428">
          Volgende oefening ↓
        </div>
        <div className="text-xs font-bold text-emerald-900" data-id="element-429">
          ⅓ + ⅓ = ? <span className="opacity-50" data-id="element-430">→ aangepaste herhaling</span>
        </div>
      </div>
    </div>;
}